/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import ReactRenderer from '@atlaskit/renderer/renderer';
import SectionMessage from '@atlaskit/section-message/message';
import { type CoreData, type ExtensionData } from '@atlassian/forge-ui-types';
import { type Extension } from '@atlassian/forge-ui/ui';
import { Loader } from '@atlassian/forge-ui/ui';

import { useInvokeAdfExportExtension } from '../../utils/useInvokeAdfExportExtension';
import { messages } from '../i18n';

const MEDIA_OPTIONS_WITH_LINKING = { allowLinking: true };

interface AdfExportRendererProps {
	accountId: string;
	contextIds: string[];
	coreData: CoreData;
	dataProviders: ProviderFactory;
	extension: Extension;
	extensionData: ExtensionData;
	extensionId: string;
	extensionPayload: Record<string, any>;
	extensionTitle?: string;
	fallbackRenderer?: () => JSX.Element;
	getContextToken?: () => Promise<string>;
	isInsideOfInlineExtension?: boolean;
}

export const AdfExportRenderer = ({
	accountId,
	contextIds,
	coreData,
	dataProviders,
	extensionData,
	extensionId,
	extensionPayload,
	fallbackRenderer,
	getContextToken,
	isInsideOfInlineExtension,
}: AdfExportRendererProps): JSX.Element | null => {
	const { adfDoc, error, isLoading } = useInvokeAdfExportExtension({
		accountId,
		contextIds,
		extensionId,
		coreData,
		extensionData,
		extensionPayload,
		getContextToken,
	});
	const intl = useIntl();

	if (error) {
		return (
			<SectionMessage appearance="error">
				{intl.formatMessage(messages.exportMacroError)}
			</SectionMessage>
		);
	}

	if (isLoading) {
		return <Loader />;
	}

	// adfDoc is null/undefined - vendor intentionally returned null/undefined to signal
	// "don't use ADF rendering for this export type". Use fallback renderer if provided,
	// otherwise render nothing.
	if (!adfDoc) {
		return fallbackRenderer ? fallbackRenderer() : null;
	}

	// Successfully got ADF doc - render it
	return (
		<RendererActionsContext>
			<ReactRenderer
				dataProviders={dataProviders}
				document={adfDoc}
				isInsideOfInlineExtension={isInsideOfInlineExtension}
				media={MEDIA_OPTIONS_WITH_LINKING}
			/>
		</RendererActionsContext>
	);
};
